import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { post } from '../../shared/apiClient';
import {
  ACTION_COL_CLASS,
  outlineCtaClassName,
  REGISTER_CARD_CLASS,
  RegisterBackButton,
  RegisterStepFooter,
} from './registerShared';

const RESEND_COOLDOWN = 60;

type RegisterStepVerifyEmailProps = {
  email: string;
  onBack: () => void;
  onVerified: () => void;
  onResend: () => void;
};

const RegisterStepVerifyEmail: React.FC<RegisterStepVerifyEmailProps> = ({ email, onBack }) => {
  const { t } = useTranslation('auth');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  const handleResend = useCallback(async () => {
    setResendError(null);
    setResendSuccess(false);
    setResending(true);
    try {
      await post('/auth/resend-verification', { email });
      setResendCooldown(RESEND_COOLDOWN);
      setResendSuccess(true);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : t('register.verify.resend_failed');
      setResendError(msg);
    } finally {
      setResending(false);
    }
  }, [email, t]);

  return (
    <div className={REGISTER_CARD_CLASS}>
      <div>
        <RegisterBackButton onBack={onBack} />
        <h1 className="text-center text-[22px] font-semibold leading-tight text-gray-900">
          {t('register.verify.title')}
        </h1>
      </div>

      <div className="flex flex-col gap-2 text-center text-sm text-gray-600">
        <p>
          <Trans
            i18nKey="auth:register.verify.sent_to"
            values={{ email: email || t('register.verify.sent_fallback') }}
            components={{ strong: <strong /> }}
          />
        </p>
        <p>{t('register.verify.click_link')}</p>
        <p className="text-gray-400">{t('register.verify.spam_hint')}</p>
      </div>

      {resendSuccess ? (
        <p className="text-center text-sm text-green-600">{t('register.verify.resend_success')}</p>
      ) : null}
      {resendError ? (
        <p className="text-center text-sm text-red-600" role="alert">
          {resendError}
        </p>
      ) : null}

      <div className={ACTION_COL_CLASS}>
        <button
          type="button"
          onClick={handleResend}
          disabled={resendCooldown > 0 || resending}
          className={outlineCtaClassName + ' disabled:opacity-50'}
        >
          {resendCooldown > 0
            ? t('register.verify.resend_in', { seconds: resendCooldown })
            : resending
              ? t('sign_in.sending')
              : t('register.verify.resend_link')}
        </button>
      </div>

      <RegisterStepFooter step={3} />
    </div>
  );
};

export default RegisterStepVerifyEmail;
